import { faEllipsis, faGear, faLock, faLockOpen, faPen, faPrint, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@shared/components/ui/dropdown-menu';

import { Location } from '../../models/Location';

type LabelSize = 'small' | 'large';

interface DropdownActionsProps {
  location: Location;
  onEdit?: (location: Location) => void;
  onDelete?: (location: Location) => void;
  onAdjust?: (location: Location) => void;
  onPrint?: (location: Location, size: LabelSize) => void;
  onToggleLock?: (location: Location) => void;
}

export const DropdownActions = ({
  location,
  onEdit,
  onDelete,
  onAdjust,
  onPrint,
  onToggleLock,
}: DropdownActionsProps) => {
  const { t } = useTranslation('locationsModule');
  const isLocked = location.is_locked;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="hover:bg-muted rounded-md" data-testid={`dropdown-trigger-${location.id}`}>
          <FontAwesomeIcon icon={faEllipsis} className="text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        className="w-44 border-card-border"
        data-testid={`dropdown-content-${location.id}`}
      >
        {onEdit && (
          <DropdownMenuItem data-testid={`dropdown-edit-${location.id}`} onClick={() => onEdit(location)}>
            <FontAwesomeIcon icon={faPen} className="mr-2 w-3.5" />
            {t('table.edit')}
          </DropdownMenuItem>
        )}

        {onDelete && (
          <DropdownMenuItem data-testid={`dropdown-delete-${location.id}`} onClick={() => onDelete(location)}>
            <FontAwesomeIcon icon={faTrash} className="mr-2 w-3.5" />
            {t('table.delete')}
          </DropdownMenuItem>
        )}

        {onAdjust && (
          <DropdownMenuItem data-testid={`dropdown-adjust-${location.id}`} onClick={() => onAdjust(location)}>
            <FontAwesomeIcon icon={faGear} className="mr-2 w-3.5" />
            {t('table.adjust')}
          </DropdownMenuItem>
        )}

        {onPrint && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger data-testid={`dropdown-print-${location.id}`}>
              <FontAwesomeIcon icon={faPrint} className="mr-2 w-3.5" />
              {t('table.print')}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuItem
                data-testid={`dropdown-print-small-${location.id}`}
                onClick={() => onPrint(location, 'small')}
              >
                {t('modal.label.size_small')}
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid={`dropdown-print-large-${location.id}`}
                onClick={() => onPrint(location, 'large')}
              >
                {t('modal.label.size_large')}
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}

        {onToggleLock && (
          <DropdownMenuItem
            data-testid={`dropdown-${isLocked ? 'unlock' : 'lock'}-${location.id}`}
            onClick={() => onToggleLock(location)}
          >
            <FontAwesomeIcon icon={isLocked ? faLockOpen : faLock} className="mr-2 w-3.5" />
            {isLocked ? t('table.unlock') : t('table.lock')}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
